import { useEffect, useState } from 'react';
import {
  bulkUpdateOrder,
  updateWasserijItemMeta,
  type ItemChange,
  type ItemShape,
  type OrderItem,
} from '../../lib/orders';
import {
  PRIMARY_SKU_REINIGEN,
  PRIMARY_SKU_WEDERVERKOPER,
  getPackagesForPrimary,
} from '../../lib/order-edit-rules';

interface EditWasserijItemDialogProps {
  isOpen: boolean;
  orderId: string;
  item: OrderItem | null;
  /**
   * ProductCode van de pakket-rij die nu onder dit kleed hangt (null = geen).
   * De primary-OrderItem draagt het pakket niet zelf; de caller leest het uit
   * de extras van de groep.
   */
  huidigPakketSku?: string | null;
  onClose: () => void;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

function inferVorm(item: OrderItem): ItemShape {
  if (item.diameterCm && item.diameterCm > 0) return 'rond';
  if (item.lengthCm && item.widthCm && item.lengthCm > 0 && item.widthCm > 0) return 'rechthoekig';
  return 'overig';
}

function isSizedItem(item: OrderItem): boolean {
  return (
    (item.areaM2 ?? 0) > 0 ||
    (item.lengthCm ?? 0) > 0 ||
    (item.widthCm ?? 0) > 0 ||
    (item.diameterCm ?? 0) > 0
  );
}

export function EditWasserijItemDialog({
  isOpen,
  orderId,
  item,
  huidigPakketSku = null,
  onClose,
  onSaved,
}: EditWasserijItemDialogProps) {
  const [lengte, setLengte] = useState('');
  const [breedte, setBreedte] = useState('');
  const [diameter, setDiameter] = useState('');
  const [oppervlakte, setOppervlakte] = useState('');
  const [bijzonderheden, setBijzonderheden] = useState('');
  const [anderePrijs, setAnderePrijs] = useState(false);
  const [verkoopprijs, setVerkoopprijs] = useState('');
  const [korting, setKorting] = useState('');
  const [packageSku, setPackageSku] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !item) return;
    setLengte(item.lengthCm ? String(item.lengthCm) : '');
    setBreedte(item.widthCm ? String(item.widthCm) : '');
    setDiameter(item.diameterCm ? String(item.diameterCm) : '');
    setOppervlakte(item.areaM2 ? String(item.areaM2) : '');
    setBijzonderheden(item.description || '');
    setAnderePrijs(!!item.anderePrijs);
    setVerkoopprijs(item.verkoopprijs != null ? String(item.verkoopprijs) : '');
    setKorting(item.korting != null ? String(item.korting) : '');
    // Huidige pakket voorselecteren. Stond hier eerder leeg ("— ongewijzigd —"),
    // waardoor het dialoog niet liet zien wat er nu op het kleed zit; in
    // combinatie met de server die de wissel stil liet vallen (05-08) was niet
    // te zien dat er niets gebeurde.
    setPackageSku(huidigPakketSku ?? '');
    setError(null);
  }, [isOpen, item, huidigPakketSku]);

  if (!isOpen || !item) return null;

  const sized = isSizedItem(item);
  const vorm = inferVorm(item);

  // Factuurregel van een reparatie: prijs komt uit Reparatie_Opdracht__c en is
  // niet herberekenbaar. Het prijsblok blijft daarom onzichtbaar — de server
  // weigert een patch die de pin uitzet (errorCode REPARATIE_PRIJS_PIN), dit is
  // de kant die voorkomt dat iemand het per ongeluk probeert.
  const isReparatieRegel = item.isReparatieRegel === true;

  // Pakketkeuze tonen voor primaire kleed-items. De pakket-familie volgt de
  // primary-SKU van het kleed; getPackagesForPrimary valt terug op de Reinigen-
  // familie voor onbekende codes.
  const primarySku =
    item.productCode === PRIMARY_SKU_WEDERVERKOPER
      ? PRIMARY_SKU_WEDERVERKOPER
      : PRIMARY_SKU_REINIGEN;
  const familieOptions = getPackagesForPrimary(primarySku);
  // Hangt er een pakket dat niet (meer) in de familie zit, dan hoort het er toch
  // bij te staan — anders toont het dialoog een ander pakket dan er ligt.
  const packageOptions =
    huidigPakketSku && !familieOptions.some((p) => p.sku === huidigPakketSku)
      ? [{ sku: huidigPakketSku, label: `${huidigPakketSku} (huidig)` }, ...familieOptions]
      : familieOptions;
  const canChangePackage = sized;
  const packageChanged = packageSku !== '' && packageSku !== (huidigPakketSku ?? '');

  const initial = {
    lengte: item.lengthCm ? String(item.lengthCm) : '',
    breedte: item.widthCm ? String(item.widthCm) : '',
    diameter: item.diameterCm ? String(item.diameterCm) : '',
    oppervlakte: item.areaM2 ? String(item.areaM2) : '',
    bijzonderheden: item.description || '',
    anderePrijs: !!item.anderePrijs,
    verkoopprijs: item.verkoopprijs != null ? String(item.verkoopprijs) : '',
    korting: item.korting != null ? String(item.korting) : '',
  };

  const dimensionsChanged =
    lengte !== initial.lengte ||
    breedte !== initial.breedte ||
    diameter !== initial.diameter ||
    oppervlakte !== initial.oppervlakte;
  const metaChanged =
    bijzonderheden !== initial.bijzonderheden ||
    anderePrijs !== initial.anderePrijs ||
    verkoopprijs !== initial.verkoopprijs ||
    korting !== initial.korting;
  const hasChanges =
    (sized && dimensionsChanged) || metaChanged || (canChangePackage && packageChanged);

  async function handleSave() {
    if (!item) return;
    setSaving(true);
    setError(null);

    // Vaste call-volgorde: meta eerst (Andere_prijs__c moet aan staan vóór de
    // bulk-update reprice draait, anders overschrijft PricingService de
    // handmatige Verkoopprijs__c). Daarna pas size-changes via bulk-update.
    const metaPatch: Record<string, unknown> = {};
    if (bijzonderheden !== initial.bijzonderheden) {
      metaPatch.Omschrijving__c = bijzonderheden;
    }
    if (anderePrijs !== initial.anderePrijs) {
      metaPatch.Andere_prijs__c = anderePrijs;
    }
    if (anderePrijs && verkoopprijs !== initial.verkoopprijs) {
      metaPatch.Verkoopprijs__c = verkoopprijs ? Number(verkoopprijs) : null;
    }
    if (korting !== initial.korting) {
      metaPatch.Korting__c = korting ? Number(korting) : null;
    }

    const sizeChanges: ItemChange[] = [];
    if (sized && (dimensionsChanged || packageChanged)) {
      // De Apex (OrderMutationService.calculateOppervlakte + applyDimensions) leidt
      // de oppervlakte af uit `vorm` + de volledige maat-set. Zonder `vorm` geeft het
      // INVALID_DIMENSIONS, en bij 'rechthoekig' moeten lengte én breedte allebei mee
      // (één van de twee = null → null oppervlakte). Daarom altijd de vorm + de hele
      // set voor die vorm meesturen — ook bij een pakket-only wijziging — niet
      // alleen het gewijzigde veld.
      const change: ItemChange = { kind: 'update', itemId: item.id, vorm };
      if (vorm === 'rechthoekig') {
        change.lengteCm = lengte ? Number(lengte) : 0;
        change.breedteCm = breedte ? Number(breedte) : 0;
      } else if (vorm === 'rond') {
        change.diameterCm = diameter ? Number(diameter) : 0;
      } else {
        change.oppervlakteM2 = oppervlakte ? Number(oppervlakte) : 0;
      }
      if (packageChanged) {
        change.packageSku = packageSku;
      }
      sizeChanges.push(change);
    }

    let metaLmd: string | null = null;
    try {
      if (Object.keys(metaPatch).length > 0) {
        const metaRes = await updateWasserijItemMeta(item.id, metaPatch);
        metaLmd = metaRes.nieuweLastModifiedDate;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan meta-velden mislukt');
      setSaving(false);
      return;
    }

    let sizeLmd: string | null = null;
    if (sizeChanges.length > 0) {
      try {
        const result = await bulkUpdateOrder(orderId, sizeChanges);
        sizeLmd = result.nieuweLastModifiedDate;
      } catch (err) {
        setError(
          err instanceof Error
            ? `Maten/pakket niet opgeslagen — andere wijzigingen wel. ${err.message}`
            : 'Maten/pakket niet opgeslagen — andere wijzigingen wel.'
        );
        setSaving(false);
        // Trigger refresh zodat de wel-gelukte meta-mutatie zichtbaar wordt.
        onSaved(metaLmd);
        return;
      }
    }

    setSaving(false);
    onSaved(sizeLmd ?? metaLmd);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Item bewerken</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {item.name || item.productCode}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {sized && vorm === 'rechthoekig' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-600">Lengte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={lengte}
                  onChange={(e) => setLengte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Breedte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={breedte}
                  onChange={(e) => setBreedte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
            </div>
          )}

          {sized && vorm === 'rond' && (
            <label className="block">
              <span className="text-xs text-slate-600">Diameter (cm)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          {sized && vorm === 'overig' && (
            <label className="block">
              <span className="text-xs text-slate-600">Oppervlakte (m²)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0.01}
                step={0.01}
                value={oppervlakte}
                onChange={(e) => setOppervlakte(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          {canChangePackage && (
            <label className="block">
              <span className="text-xs text-slate-600">Pakket</span>
              <select
                value={packageSku}
                onChange={(e) => setPackageSku(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
              >
                {/* Alleen aanbieden als er nog geen pakket hangt: een bestaande
                    pakket-rij kan niet los verwijderd worden, alleen gewisseld. */}
                {!huidigPakketSku && <option value="">— geen pakket —</option>}
                {packageOptions.map((p) => (
                  <option key={p.sku} value={p.sku}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-xs text-slate-600">Bijzonderheden</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={bijzonderheden}
              onChange={(e) => setBijzonderheden(e.target.value)}
              placeholder="Bijv. randen iets gerafeld, vlek bij het midden"
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </label>

          {isReparatieRegel ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs font-medium text-amber-900">
                Prijs staat vast op de reparatie-opdracht
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-800">
                Dit is de factuurregel van een reparatie
                {item.verkoopprijs != null
                  ? ` van € ${(item.verkoopprijs * 1.21).toFixed(2).replace('.', ',')} incl. BTW`
                  : ''}
                . Het bedrag komt uit de opdracht en is hier niet te wijzigen: zonder die
                pin herprijst het systeem de regel naar € 0 en is het bedrag niet meer terug
                te rekenen. Aanpassen doe je op de reparatie-opdracht zelf.
              </p>
            </div>
          ) : (
            <>
              <label className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  checked={anderePrijs}
                  onChange={(e) => setAnderePrijs(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">Andere prijs (handmatige eenheidsprijs)</span>
              </label>

              {anderePrijs && (
                <label className="block">
                  <span className="text-xs text-slate-600">Handmatige prijs (excl. BTW)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step={0.01}
                    min={0}
                    value={verkoopprijs}
                    onChange={(e) => setVerkoopprijs(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              )}

              <label className="block">
                <span className="text-xs text-slate-600">Korting %</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step={0.01}
                  min={0}
                  max={100}
                  value={korting}
                  onChange={(e) => setKorting(e.target.value)}
                  placeholder="0"
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
                <span className="block mt-1 text-[11px] text-slate-400">
                  Bijv. 10 = 10% korting
                </span>
              </label>
            </>
          )}

          {sized && (
            <p className="text-xs text-slate-400">
              Vorm wisselen kan niet — verwijder en voeg opnieuw toe als de vorm anders is.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
